import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { database } from "../../firebaseConfig";
import { ref, onValue, remove, update, get, set, push } from "firebase/database";
import DropdownMenu from "./DropdownMenu";
import ReissueInvoiceModal from "./ReissueInvoiceModal";
import {
  buildNumberIndex,
  collectDocumentNumbers,
  suggestNextNumber,
} from "./utils/documentNumbers";
import "./OfferDashboard/OfferDashboard.css";

const TYPE_FILTERS = [
  { id: "offer", label: "Offers" },
  { id: "invoice", label: "Invoices" },
  { id: "valueOffer", label: "Value-based" },
  { id: "contract", label: "Contracts" },
];

const SORT_OPTIONS = [
  { id: "createdAt", label: "Date", description: "When the document was created" },
  { id: "title", label: "Name", description: "Alphabetically by title or customer" },
  { id: "discountedTotal", label: "Value", description: "By total amount" },
];

// The list mixes four document shapes; this collapses them into the single
// category the filter chips and badges reason about.
const docKind = (doc) => {
  if (doc.type === "contract") return "contract";
  if (doc.type === "valueOffer") return "valueOffer";
  return doc.documentType === "invoice" ? "invoice" : "offer";
};

const OfferDashboard = ({ userId, onCreateOffer, onEditOffer, onCreateContract, onEditContract = () => {} }) => {
  const navigate = useNavigate();
  
  const [offers, setOffers] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [valueOffers, setValueOffers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [notification, setNotification] = useState({ message: "", type: "" });
  const [expandedOfferId, setExpandedOfferId] = useState(null);
  const [expandedContractId, setExpandedContractId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortDirection, setSortDirection] = useState("desc");
  const [copiedLinkId, setCopiedLinkId] = useState(null);
  // Document the reissue dialog is currently building a follow-up invoice from.
  const [reissueSource, setReissueSource] = useState(null);

  // Load offers from the database
  useEffect(() => {
    if (!userId) return;

    const offersRef = ref(database, `/World/${userId}/offers`);
    
    const unsubscribeOffers = onValue(offersRef, (snapshot) => {
      if (snapshot.exists()) {
        const offersData = snapshot.val();
        const offersArray = Object.entries(offersData).map(([id, data]) => ({
          id,
          ...data,
          type: 'offer'
        }));
        setOffers(offersArray);
      } else {
        setOffers([]);
      }
    });

    return () => unsubscribeOffers();
  }, [userId]);

  // Load contracts from the database
  useEffect(() => {
    if (!userId) return;

    const contractsRef = ref(database, `/World/${userId}/contracts`);
    
    const unsubscribeContracts = onValue(contractsRef, (snapshot) => {
      if (snapshot.exists()) {
        const contractsData = snapshot.val();
        const contractsArray = Object.entries(contractsData).map(([id, data]) => ({
          id,
          ...data,
          type: 'contract'
        }));
        setContracts(contractsArray);
      } else {
        setContracts([]);
      }
      setIsLoading(false);
    });

    return () => unsubscribeContracts();
  }, [userId]);

  // Load value-based offers (KI-generated offers)
  useEffect(() => {
    if (!userId) return;
    const valueOffersRef = ref(database, `/World/${userId}/valueOffers`);
    const unsub = onValue(valueOffersRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        const arr = Object.entries(data).map(([id, v]) => ({
          id,
          ...v,
          type: 'valueOffer',
        }));
        setValueOffers(arr);
      } else {
        setValueOffers([]);
      }
    });
    return () => unsub();
  }, [userId]);

  // Delete offer
  const handleDeleteOffer = (offerId) => {
    if (!window.confirm("Are you sure you want to delete this offer?")) return;

    const offerRef = ref(database, `/World/${userId}/offers/${offerId}`);
    remove(offerRef)
      .then(() => {
        showNotification("Offer successfully deleted", "success");
      })
      .catch((error) => {
        console.error("Error deleting offer:", error);
        showNotification("Error deleting offer", "error");
      });
  };

  // Delete contract
  const handleDeleteContract = (contractId) => {
    if (!window.confirm("Are you sure you want to delete this contract?")) return;

    const contractRef = ref(database, `/World/${userId}/contracts/${contractId}`);
    remove(contractRef)
      .then(() => {
        showNotification("Contract successfully deleted", "success");
      })
      .catch((error) => {
        console.error("Error deleting contract:", error);
        showNotification("Error deleting contract", "error");
      });
  };

  // Edit contract
  const handleEditContract = (contractId) => {
    if (onEditContract) {
      const contract = contracts.find(c => c.id === contractId);
      if (contract) {
        onEditContract(contract);
      }
    }
  };

  // Edit offer - navigate to URL with offer ID
  const handleEditOffer = (offerId) => {
    // Navigate to the offer edit page using URL routing
    navigate(`/deals/${offerId}`);
  };

  // ---------- Value-Based Offer actions ----------
  const handleEditValueOffer = (id) => navigate(`/value-offer/${id}`);

  const handleDeleteValueOffer = async (id) => {
    if (!window.confirm("Value-Based Offer wirklich löschen?")) return;
    try {
      await remove(ref(database, `/World/${userId}/valueOffers/${id}`));
      // Best-effort: also clean up public copy if it exists
      await remove(ref(database, `/publicValueOffers/${userId}+${id}`)).catch(() => {});
      showNotification("Value-Based Offer gelöscht", "success");
    } catch (e) {
      console.error("Error deleting value offer:", e);
      showNotification("Fehler beim Löschen", "error");
    }
  };

  const handleShareValueOffer = async (offer) => {
    if (!offer || !offer.id) return;
    try {
      const customId = `${userId}+${offer.id}`;
      // Backfill `generated.recipient` from the configurator config before
      // publishing — otherwise the public letterhead would render without
      // the customer's address block whenever the salesperson never
      // manually edited those inline fields.
      const cfg = offer.config || {};
      const existingRecipient = offer.generated?.recipient || {};
      const existingMeetingContacts = Array.isArray(
        existingRecipient.meetingContacts
      )
        ? existingRecipient.meetingContacts
        : null;
      const cfgMeetingContacts = Array.isArray(cfg.meetingContacts)
        ? cfg.meetingContacts
        : [];
      const recipient = {
        companyName: existingRecipient.companyName || cfg.companyName || "",
        invoiceRecipient:
          existingRecipient.invoiceRecipient || cfg.invoiceRecipient || "",
        companyAddress:
          existingRecipient.companyAddress || cfg.companyAddress || "",
        legalRepresentative:
          existingRecipient.legalRepresentative ||
          cfg.legalRepresentative ||
          "",
        meetingContacts: existingMeetingContacts || cfgMeetingContacts,
        addresseeMode:
          existingRecipient.addresseeMode || cfg.addresseeMode || "meeting",
      };
      const generatedForShare = offer.generated
        ? { ...offer.generated, recipient }
        : null;
      // Publish only the customer-facing generated content (no internal config)
      const publicPayload = {
        userId,
        offerId: offer.id,
        generated: generatedForShare,
        createdAt: offer.createdAt || Date.now(),
        updatedAt: Date.now(),
        publishedAt: Date.now(),
      };
      await set(
        ref(database, `/publicValueOffers/${customId}`),
        publicPayload
      );
      const url = `${window.location.origin}/v/${customId}`;
      try {
        await navigator.clipboard.writeText(url);
        setCopiedLinkId(offer.id);
        showNotification("Share-Link kopiert · " + url, "success");
        setTimeout(() => setCopiedLinkId(null), 3000);
      } catch (_) {
        showNotification("Geteilt: " + url, "success");
      }
    } catch (e) {
      console.error("Error sharing value offer:", e);
      showNotification("Fehler beim Teilen", "error");
    }
  };

  const openValueOfferLink = async (offer) => {
    await handleShareValueOffer(offer);
    const url = `${window.location.origin}/v/${userId}+${offer.id}`;
    window.open(url, "_blank");
  };

  // Recipient shown under the title in the list. Field names differ per type.
  const docCustomerName = (doc) => {
    if (doc?.type === 'valueOffer') {
      return (
        doc?.config?.companyName?.trim() ||
        doc?.config?.invoiceRecipient?.trim() ||
        doc?.generated?.recipient?.company?.trim() ||
        ""
      );
    }
    return doc?.customerName?.trim() || "";
  };

  // Compute a single display title + price range for a value-based offer.
  const valueOfferDisplay = (vo) => {
    const title = vo?.generated?.title?.trim() || "Value-Based Offer";
    const opts = vo?.generated?.leistungsumfang?.options || [];
    const prices = opts
      .map((o) => Number(o.price) || 0)
      .filter((p) => p > 0);
    let priceLabel = "—";
    if (prices.length === 1) {
      priceLabel = `${formatPrice(prices[0])}/Mo`;
    } else if (prices.length > 1) {
      const min = Math.min(...prices);
      const max = Math.max(...prices);
      priceLabel = `${formatPrice(min)} – ${formatPrice(max)}/Mo`;
    }
    return { title, priceLabel, optionsCount: opts.length };
  };

  // Duplicate offer
  const handleDuplicateOffer = async (offer) => {
    try {
      // Create a copy of the offer with a new title
      const duplicatedOffer = {
        ...offer,
        title: `${offer.title} (Copy)`,
        createdAt: Date.now(),
        updatedAt: Date.now(),
        isPublic: false, // Reset public status for duplicates
      };

      // A copy must never reuse the invoice number of the original.
      if (offer.documentType === 'invoice') {
        const index = buildNumberIndex(
          collectDocumentNumbers(
            Object.fromEntries(offers.map((doc) => [doc.id, doc])),
            'invoice'
          )
        );
        duplicatedOffer.invoiceNumber = suggestNextNumber(index) || '';
      }
      
      // Remove the original ID so Firebase creates a new one
      delete duplicatedOffer.id;
      delete duplicatedOffer.type; // Remove the type field we added for display
      
      // Save to Firebase
      const offersRef = ref(database, `/World/${userId}/offers`);
      const newOfferRef = push(offersRef);
      await set(newOfferRef, duplicatedOffer);

      showNotification(
        duplicatedOffer.documentType === 'invoice' && duplicatedOffer.invoiceNumber
          ? `Invoice duplicated as ${duplicatedOffer.invoiceNumber}`
          : "Offer successfully duplicated",
        "success"
      );
    } catch (error) {
      console.error("Error duplicating offer:", error);
      showNotification("Error duplicating offer", "error");
    }
  };

  // Duplicate a Value-Based Offer.
  //
  // Mirrors handleDuplicateOffer but writes to /World/<uid>/valueOffers and
  // appends " (Kopie)" inside generated.title (the field actually rendered
  // in the dashboard list — the top-level `title` doesn't exist on
  // valueOffer docs). We also explicitly drop the publication metadata
  // (isPublic + any client-side `type` tag from the dashboard merge) so
  // the duplicate starts as a private draft.
  const handleDuplicateValueOffer = async (offer) => {
    try {
      const sourceTitle = offer?.generated?.title || "Value-Based Offer";
      const duplicated = {
        ...offer,
        generated: {
          ...(offer?.generated || {}),
          title: `${sourceTitle} (Kopie)`,
        },
        createdAt: Date.now(),
        updatedAt: Date.now(),
        isPublic: false,
      };
      delete duplicated.id;
      delete duplicated.type;

      const valueOffersRef = ref(database, `/World/${userId}/valueOffers`);
      const newRef = push(valueOffersRef);
      await set(newRef, duplicated);

      showNotification("Value-Based Offer dupliziert", "success");
    } catch (error) {
      console.error("Error duplicating value offer:", error);
      showNotification("Fehler beim Duplizieren", "error");
    }
  };

  // Expand/collapse offer details
  const toggleExpandOffer = (offerId) => {
    setExpandedOfferId(expandedOfferId === offerId ? null : offerId);
    setExpandedContractId(null); // Close contract if open
  };

  // Expand/collapse contract details
  const toggleExpandContract = (contractId) => {
    setExpandedContractId(expandedContractId === contractId ? null : contractId);
    setExpandedOfferId(null); // Close offer if open
  };

  // Show and hide notification
  const showNotification = (message, type) => {
    setNotification({ message, type });
    
    // Automatically hide after 3 seconds
    setTimeout(() => {
      setNotification({ message: "", type: "" });
    }, 3000);
  };

  // Sort offers
  const handleSort = (field) => {
    if (sortBy === field) {
      // If already sorted by this field, reverse direction
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      // Otherwise sort by new field (default descending)
      setSortBy(field);
      setSortDirection("desc");
    }
  };

  // Combine offers, contracts, and value-based offers, then filter and sort
  const allDocuments = [...offers, ...contracts, ...valueOffers];
  
  const docTitleForSearch = (doc) => {
    if (doc.type === 'contract') return (doc.customerName || '').toLowerCase();
    if (doc.type === 'valueOffer') {
      const t = doc?.generated?.title || '';
      const c = doc?.config?.companyName || '';
      return `${t} ${c}`.toLowerCase();
    }
    return (doc.title || '').toLowerCase();
  };

  const searchedDocuments = allDocuments.filter((doc) => {
    const term = searchTerm.toLowerCase();
    if (!term) return true;
    const haystack = docTitleForSearch(doc);
    if (haystack.includes(term)) return true;
    if (docCustomerName(doc).toLowerCase().includes(term)) return true;
    if (doc.type === 'offer' && doc.description?.toLowerCase().includes(term)) {
      return true;
    }
    return false;
  });

  // Counts drive the filter chips, so a chip is only offered when it would
  // actually yield rows for the current search.
  const typeCounts = searchedDocuments.reduce((counts, doc) => {
    const kind = docKind(doc);
    counts[kind] = (counts[kind] || 0) + 1;
    return counts;
  }, {});

  const filteredAndSortedDocuments = searchedDocuments
    .filter((doc) => typeFilter === "all" || docKind(doc) === typeFilter)
    .sort((a, b) => {
      let comparison = 0;
      if (sortBy === "title") {
        comparison = docTitleForSearch(a).localeCompare(docTitleForSearch(b));
      } else if (sortBy === "createdAt") {
        comparison = (a.createdAt || 0) - (b.createdAt || 0);
      } else if (sortBy === "discountedTotal") {
        const priceOf = (d) => {
          if (d.type === 'contract') return -1;
          if (d.type === 'valueOffer') {
            const opts = d?.generated?.leistungsumfang?.options || [];
            // Use the Plus tier (mid) as representative for sorting.
            return Number(opts[1]?.price) || Number(opts[0]?.price) || 0;
          }
          return Number(d.discountedTotal) || 0;
        };
        comparison = priceOf(a) - priceOf(b);
      }
      return sortDirection === "asc" ? comparison : -comparison;
    });

  // Display formatted date
  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleDateString("en-US", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric"
    });
  };

  // Format price
  const formatPrice = (price) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "EUR"
    }).format(price);
  };

  // Generate link to offer
  const generateOfferLink = (offerId) => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/custom-offer/${userId}+${offerId}`;
  };

  // Generate link to contract
  const generateContractLink = (contractId) => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/custom-contract/${userId}+${contractId}`;
  };

  // Copy link to clipboard and mark offer as public
  const copyOfferLink = async (offerId) => {
    try {
      // Mark offer as public when sharing
      await handleTogglePublic(offerId, true);
      
      const link = generateOfferLink(offerId);
      await navigator.clipboard.writeText(link);
      
      setCopiedLinkId(offerId);
      showNotification("Link copied to clipboard and offer made public", "success");
      
      // Reset after 3 seconds
      setTimeout(() => {
        setCopiedLinkId(null);
      }, 3000);
    } catch (error) {
      console.error("Error copying link or updating offer:", error);
      showNotification("Error copying link", "error");
    }
  };

  // Copy contract link to clipboard and mark contract as public
  const copyContractLink = async (contractId) => {
    try {
      // Mark contract as public when sharing
      await handleToggleContractPublic(contractId, true);
      
      const link = generateContractLink(contractId);
      await navigator.clipboard.writeText(link);
      
      setCopiedLinkId(contractId);
      showNotification("Link copied to clipboard and contract made public", "success");
      
      // Reset after 3 seconds
      setTimeout(() => {
        setCopiedLinkId(null);
      }, 3000);
    } catch (error) {
      console.error("Error copying link or updating contract:", error);
      showNotification("Error copying link", "error");
    }
  };

  // Open link in new tab and mark offer as public
  const openOfferLink = async (offerId) => {
    try {
      // Mark offer as public when opening
      await handleTogglePublic(offerId, true);
      
      const link = generateOfferLink(offerId);
      window.open(link, '_blank');
    } catch (error) {
      console.error("Error updating offer or opening link:", error);
      // Still open link even if update fails
      const link = generateOfferLink(offerId);
      window.open(link, '_blank');
    }
  };

  // Open contract link in new tab and mark contract as public
  const openContractLink = async (contractId) => {
    try {
      // Mark contract as public when opening
      await handleToggleContractPublic(contractId, true);
      
      const link = generateContractLink(contractId);
      window.open(link, '_blank');
    } catch (error) {
      console.error("Error updating contract or opening link:", error);
      // Still open link even if update fails
      const link = generateContractLink(contractId);
      window.open(link, '_blank');
    }
  };

  // Toggle public status of offer
  const handleTogglePublic = async (offerId, isPublic) => {
    try {
      const offerRef = ref(database, `/World/${userId}/offers/${offerId}`);
      await update(offerRef, { isPublic });
      
      if (isPublic) {
        // Copy offer to public offers with all necessary data
        await copyOfferToPublic(offerId);
      } else {
        // Remove from public offers
        const customId = `${userId}+${offerId}`;
        const publicOfferRef = ref(database, `/publicOffers/${customId}`);
        await remove(publicOfferRef);
      }
      
      showNotification(
        `Offer ${isPublic ? 'made public' : 'made private'}`,
        "success"
      );
    } catch (error) {
      console.error("Error updating offer visibility:", error);
      showNotification("Error updating offer visibility", "error");
    }
  };

  // Copy offer with all data to public offers
  const copyOfferToPublic = async (offerId) => {
    try {
      // Get offer data
      const offerRef = ref(database, `/World/${userId}/offers/${offerId}`);
      const offerSnapshot = await get(offerRef);
      
      if (!offerSnapshot.exists()) return;
      
      const offerData = offerSnapshot.val();
      const customId = `${userId}+${offerId}`;
      
      // Get services data
      let servicesData = [];
      if (offerData.services) {
        const servicesRef = ref(database, `/World/${userId}/services`);
        const servicesSnapshot = await get(servicesRef);
        
        if (servicesSnapshot.exists()) {
          const allServices = servicesSnapshot.val();
          servicesData = offerData.services
            .map(serviceId => {
              const service = allServices[serviceId];
              return service ? { id: serviceId, ...service } : null;
            })
            .filter(service => service !== null);
        }
      }
      
      // Get team member data if available
      let teamMemberData = null;
      if (offerData.teamMemberId) {
        const teamMemberRef = ref(database, `/World/${userId}/team/members/${offerData.teamMemberId}`);
        const teamMemberSnapshot = await get(teamMemberRef);
        if (teamMemberSnapshot.exists()) {
          teamMemberData = teamMemberSnapshot.val();
        }
      }
      
      // Create public offer with all data
      const publicOfferData = {
        ...offerData,
        services: servicesData,
        teamMember: teamMemberData,
        userId,
        offerId,
        createdAt: Date.now()
      };
      
      const publicOfferRef = ref(database, `/publicOffers/${customId}`);
      await set(publicOfferRef, publicOfferData);
      
    } catch (error) {
      console.error("Error copying offer to public:", error);
      throw error;
    }
  };

  // Toggle public status of contract
  const handleToggleContractPublic = async (contractId, isPublic) => {
    try {
      const contractRef = ref(database, `/World/${userId}/contracts/${contractId}`);
      await update(contractRef, { isPublic });
      
      if (isPublic) {
        // Copy contract to public contracts with all necessary data
        await copyContractToPublic(contractId);
      } else {
        // Remove from public contracts
        const customId = `${userId}+${contractId}`;
        const publicContractRef = ref(database, `/publicContracts/${customId}`);
        await remove(publicContractRef);
      }
      
      showNotification(
        `Contract ${isPublic ? 'made public' : 'made private'}`,
        "success"
      );
    } catch (error) {
      console.error("Error updating contract visibility:", error);
      showNotification("Error updating contract visibility", "error");
    }
  };

  // Copy contract with all data to public contracts
  const copyContractToPublic = async (contractId) => {
    try {
      // Get contract data
      const contractRef = ref(database, `/World/${userId}/contracts/${contractId}`);
      const contractSnapshot = await get(contractRef);
      
      if (!contractSnapshot.exists()) return;
      
      const contractData = contractSnapshot.val();
      const customId = `${userId}+${contractId}`;
      
      // Get template data if available
      let templateData = null;
      if (contractData.templateId) {
        const templateRef = ref(database, `/World/${userId}/contractTemplates/${contractData.templateId}`);
        const templateSnapshot = await get(templateRef);
        if (templateSnapshot.exists()) {
          templateData = templateSnapshot.val();
        }
      }
      
      // Get team member data if available
      let teamMemberData = null;
      if (contractData.teamMemberId) {
        const teamMemberRef = ref(database, `/World/${userId}/team/members/${contractData.teamMemberId}`);
        const teamMemberSnapshot = await get(teamMemberRef);
        if (teamMemberSnapshot.exists()) {
          teamMemberData = teamMemberSnapshot.val();
        }
      }
      
      // Create public contract with all data
      const publicContractData = {
        ...contractData,
        template: templateData,
        teamMember: teamMemberData,
        userId,
        contractId,
        createdAt: Date.now()
      };
      
      const publicContractRef = ref(database, `/publicContracts/${customId}`);
      await set(publicContractRef, publicContractData);
      
    } catch (error) {
      console.error("Error copying contract to public:", error);
      throw error;
    }
  };

  // ---------- Menu definitions ----------
  const createMenuItems = [
    {
      id: "offer",
      icon: "fa-file-lines",
      label: "Offer",
      description: "Pick services from your library, add discounts and terms",
      onSelect: () => onCreateOffer("offer"),
    },
    {
      id: "invoice",
      icon: "fa-file-invoice",
      label: "Invoice",
      description: "The same builder, prefilled with invoice number and date",
      onSelect: () => onCreateOffer("invoice"),
    },
    {
      id: "valueOffer",
      icon: "fa-gem",
      accent: "#b08531",
      label: "Value-Based Offer",
      description: "AI drafts a pitch with three priced options from your value sets",
      onSelect: () => navigate("/value-offer"),
    },
    ...(onCreateContract
      ? [
          {
            id: "contract",
            separatorBefore: true,
            icon: "fa-file-contract",
            label: "Contract",
            description: "Fill one of your contract templates for a customer",
            onSelect: () => onCreateContract(),
          },
        ]
      : []),
  ];

  const sortMenuItems = [
    ...SORT_OPTIONS.map((option) => ({
      ...option,
      active: sortBy === option.id,
      onSelect: () => handleSort(option.id),
    })),
    {
      id: "direction",
      separatorBefore: true,
      icon: sortDirection === "asc" ? "fa-arrow-up-short-wide" : "fa-arrow-down-wide-short",
      label: sortDirection === "asc" ? "Ascending" : "Descending",
      description: "Click to flip the sort order",
      onSelect: () => setSortDirection(sortDirection === "asc" ? "desc" : "asc"),
    },
  ];

  const offerRowActions = (doc) => [
    {
      id: "open",
      icon: "fa-arrow-up-right-from-square",
      label: "Open public page",
      description: "Publishes the offer and opens the customer view",
      onSelect: () => openOfferLink(doc.id),
    },
    {
      id: "copy",
      icon: "fa-link",
      label: "Copy share link",
      description: "Publishes the offer and copies the link to your clipboard",
      onSelect: () => copyOfferLink(doc.id),
    },
    {
      id: "edit",
      icon: "fa-pen-to-square",
      label: "Edit",
      description: "Open in the offer builder",
      onSelect: () => handleEditOffer(doc.id),
    },
    {
      id: "duplicate",
      icon: "fa-copy",
      label: "Duplicate",
      description: "Create a private copy to work from",
      onSelect: () => handleDuplicateOffer(doc),
    },
    {
      id: "reissue",
      icon: "fa-file-invoice-dollar",
      label: doc.documentType === "invoice" ? "Reissue" : "Turn into invoice",
      description:
        "Same recipient and positions, new number and dates — adjust the amounts",
      onSelect: () => setReissueSource(doc),
    },
    {
      id: "delete",
      separatorBefore: true,
      variant: "danger",
      icon: "fa-trash-can",
      label: "Delete",
      description: "Removes the document and its public page",
      onSelect: () => handleDeleteOffer(doc.id),
    },
  ];

  const contractRowActions = (doc) => [
    {
      id: "open",
      icon: "fa-arrow-up-right-from-square",
      label: "Open public page",
      description: "Publishes the contract and opens the signing view",
      onSelect: () => openContractLink(doc.id),
    },
    {
      id: "copy",
      icon: "fa-link",
      label: "Copy share link",
      description: "Publishes the contract and copies the link to your clipboard",
      onSelect: () => copyContractLink(doc.id),
    },
    {
      id: "edit",
      icon: "fa-pen-to-square",
      label: "Edit",
      description: "Open in the contract builder",
      onSelect: () => handleEditContract(doc.id),
    },
    {
      id: "delete",
      separatorBefore: true,
      variant: "danger",
      icon: "fa-trash-can",
      label: "Delete",
      description: "Removes the contract and its public page",
      onSelect: () => handleDeleteContract(doc.id),
    },
  ];

  const valueOfferRowActions = (doc) => [
    {
      id: "open",
      icon: "fa-arrow-up-right-from-square",
      label: "Open public page",
      description: "Publishes the latest version and opens the customer view",
      onSelect: () => openValueOfferLink(doc),
    },
    {
      id: "copy",
      icon: "fa-link",
      label: "Copy share link",
      description: "Publishes the latest version and copies the link",
      onSelect: () => handleShareValueOffer(doc),
    },
    {
      id: "edit",
      icon: "fa-pen-to-square",
      label: "Edit",
      description: "Open in the value-based configurator",
      onSelect: () => handleEditValueOffer(doc.id),
    },
    {
      id: "duplicate",
      icon: "fa-copy",
      label: "Duplicate",
      description: "Create a private copy to work from",
      onSelect: () => handleDuplicateValueOffer(doc),
    },
    {
      id: "delete",
      separatorBefore: true,
      variant: "danger",
      icon: "fa-trash-can",
      label: "Delete",
      description: "Removes the offer and its public page",
      onSelect: () => handleDeleteValueOffer(doc.id),
    },
  ];

  const renderRowMenu = (items, label) => (
    <div
      className="offer-dashboard__cell offer-dashboard__cell--actions"
      onClick={(event) => event.stopPropagation()}
    >
      <DropdownMenu
        items={items}
        align="end"
        width={306}
        ariaLabel={`Actions for ${label}`}
        triggerTitle="Actions"
        triggerClassName="offer-dashboard__action-btn"
        triggerContent={<i className="fas fa-ellipsis" aria-hidden="true"></i>}
      />
    </div>
  );

  const renderMeta = (entries) => {
    const visible = entries.filter((entry) => entry.value);
    if (visible.length === 0) return null;
    return (
      <dl className="offer-dashboard__meta">
        {visible.map((entry) => (
          <div className="offer-dashboard__meta-row" key={entry.label}>
            <dt className="offer-dashboard__meta-label">{entry.label}</dt>
            <dd className="offer-dashboard__meta-value">{entry.value}</dd>
          </div>
        ))}
      </dl>
    );
  };

  const renderShareCard = ({ link, isPublic, onToggle, onCopy, onOpen, copied, hint }) => (
    <div className="offer-dashboard__share">
      <div className="offer-dashboard__share-head">
        <span className="offer-dashboard__share-title">
          <i className="fas fa-globe" aria-hidden="true"></i>
          Public link
        </span>
        {onToggle && (
          <button
            type="button"
            role="switch"
            aria-checked={!!isPublic}
            aria-label="Toggle public access"
            className={`offer-dashboard__switch ${isPublic ? "is-on" : ""}`}
            onClick={onToggle}
          >
            <span className="offer-dashboard__switch-knob" />
          </button>
        )}
      </div>

      <p className="offer-dashboard__share-hint">{hint}</p>

      <div className="offer-dashboard__share-row">
        <input
          className="offer-dashboard__link-input"
          value={link}
          readOnly
          onClick={(event) => event.target.select()}
        />
        <button
          type="button"
          className="offer-dashboard__ghost-btn"
          onClick={onCopy}
          title="Copy link"
        >
          <i className={`fas ${copied ? "fa-check" : "fa-link"}`} aria-hidden="true"></i>
          {copied ? "Copied" : "Copy"}
        </button>
        <button
          type="button"
          className="offer-dashboard__ghost-btn"
          onClick={onOpen}
          title="Open in a new tab"
        >
          <i className="fas fa-arrow-up-right-from-square" aria-hidden="true"></i>
          Open
        </button>
      </div>
    </div>
  );

  return (
    <div className="offer-dashboard">
      {notification.message && (
        <div className={`offer-dashboard__notification offer-dashboard__notification--${notification.type}`}>
          {notification.message}
        </div>
      )}
      
      <div className="offer-dashboard__header">
        <div className="offer-dashboard__heading">
          <h2 className="offer-dashboard__title">Offers</h2>
          <p className="offer-dashboard__subtitle">
            Offers, invoices, contracts and value-based pitches in one place
          </p>
        </div>

        <div className="offer-dashboard__toolbar">
          <div className="offer-dashboard__search">
            <i className="fas fa-magnifying-glass offer-dashboard__search-icon" aria-hidden="true"></i>
            <input
              type="text"
              placeholder="Search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="offer-dashboard__search-input"
              aria-label="Search documents"
            />
            {searchTerm && (
              <button
                className="offer-dashboard__clear-search"
                onClick={() => setSearchTerm("")}
                aria-label="Clear search"
              >
                <i className="fas fa-circle-xmark" aria-hidden="true"></i>
              </button>
            )}
          </div>

          <DropdownMenu
            items={createMenuItems}
            align="end"
            width={318}
            ariaLabel="Create a new document"
            triggerTitle="Create a new document"
            triggerClassName="offer-dashboard__create-trigger"
            triggerContent={
              <>
                <i className="fas fa-plus" aria-hidden="true"></i>
                <span className="offer-dashboard__create-label">New</span>
              </>
            }
          />
        </div>
      </div>
      
      {isLoading ? (
        <div className="offer-dashboard__loading">
          <div className="offer-dashboard__spinner"></div>
          <p>Loading offers...</p>
        </div>
      ) : offers.length === 0 && contracts.length === 0 && valueOffers.length === 0 ? (
        <div className="offer-dashboard__empty-state">
          <div className="offer-dashboard__empty-icon">
            <img 
              src="/img/bag.svg" 
              alt="No offers yet" 
              onError={(e) => {
                e.target.onerror = null;
                e.target.style.display = "none";
                e.target.parentNode.innerHTML = "📝";
              }}
              style={{ maxWidth: "40px", maxHeight: "40px" }}
            />
          </div>
          <h3 className="offer-dashboard__empty-title">Nothing here yet</h3>
          <p className="offer-dashboard__empty-description">
            Create your first offer, invoice or contract — it will show up in this list
            with its share link and status.
          </p>
          <button 
            className="offer-dashboard__create-btn"
            onClick={() => onCreateOffer("offer")}
          >
            Create first document
          </button>
        </div>
      ) : (
        <div className="offer-dashboard__content">
          <div className="offer-dashboard__filter-bar">
            <div className="offer-dashboard__chips" role="tablist" aria-label="Filter by document type">
              <button
                type="button"
                role="tab"
                aria-selected={typeFilter === "all"}
                className={`offer-dashboard__chip ${typeFilter === "all" ? "is-active" : ""}`}
                onClick={() => setTypeFilter("all")}
              >
                All
                <span className="offer-dashboard__chip-count">{searchedDocuments.length}</span>
              </button>
              {TYPE_FILTERS.filter((filter) => typeCounts[filter.id]).map((filter) => (
                <button
                  key={filter.id}
                  type="button"
                  role="tab"
                  aria-selected={typeFilter === filter.id}
                  className={`offer-dashboard__chip ${typeFilter === filter.id ? "is-active" : ""}`}
                  onClick={() => setTypeFilter(filter.id)}
                >
                  {filter.label}
                  <span className="offer-dashboard__chip-count">{typeCounts[filter.id]}</span>
                </button>
              ))}
            </div>

            <DropdownMenu
              items={sortMenuItems}
              align="end"
              width={284}
              ariaLabel="Change sorting"
              triggerTitle="Change sorting"
              triggerClassName="offer-dashboard__sort-trigger"
              triggerContent={
                <>
                  <i
                    className={`fas ${
                      sortDirection === "asc"
                        ? "fa-arrow-up-short-wide"
                        : "fa-arrow-down-wide-short"
                    }`}
                    aria-hidden="true"
                  ></i>
                  {SORT_OPTIONS.find((option) => option.id === sortBy)?.label}
                </>
              }
            />
          </div>

          {filteredAndSortedDocuments.length === 0 ? (
            <div className="offer-dashboard__no-results">
              <i className="fas fa-magnifying-glass" aria-hidden="true"></i>
              <p>No document matches your search.</p>
              <button
                type="button"
                className="offer-dashboard__ghost-btn"
                onClick={() => {
                  setSearchTerm("");
                  setTypeFilter("all");
                }}
              >
                Reset filters
              </button>
            </div>
          ) : (
          <div className="offer-dashboard__table">
            <div className="offer-dashboard__table-header">
              <div className="offer-dashboard__header-cell offer-dashboard__cell--date">Date</div>
              <div className="offer-dashboard__header-cell offer-dashboard__cell--title">Title</div>
              <div className="offer-dashboard__header-cell offer-dashboard__cell--price">Value</div>
              <div className="offer-dashboard__header-cell offer-dashboard__cell--actions"></div>
            </div>
            
            <div className="offer-dashboard__table-body">
              {filteredAndSortedDocuments.map(doc => {
                if (doc.type === 'contract') {
                  // Render contract row
                  return (
                    <React.Fragment key={doc.id}>
                      <div 
                        className={`offer-dashboard__table-row ${expandedContractId === doc.id ? "offer-dashboard__table-row--expanded" : ""}`}
                        onClick={() => toggleExpandContract(doc.id)}
                      >
                        <div className="offer-dashboard__cell offer-dashboard__cell--date">
                          {doc.contractDate ? formatDate(new Date(doc.contractDate).getTime()) : formatDate(doc.createdAt)}
                        </div>
                        <div className="offer-dashboard__cell offer-dashboard__cell--title">
                          <div className="offer-dashboard__title-stack">
                            <span className="offer-dashboard__offer-title">{doc.customerName || 'Contract'}</span>
                            <span className="offer-dashboard__badge offer-dashboard__badge--contract">
                              Contract
                            </span>
                            {doc.isPublic && (
                              <span className="offer-dashboard__badge offer-dashboard__badge--public" title="Reachable via public link">
                                <i className="fas fa-globe" aria-hidden="true"></i>
                                Shared
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="offer-dashboard__cell offer-dashboard__cell--price">
                          <span className="offer-dashboard__price-empty">—</span>
                        </div>
                        {renderRowMenu(contractRowActions(doc), doc.customerName || 'contract')}
                      </div>
                      
                      {expandedContractId === doc.id && (
                        <div className="offer-dashboard__expanded-content">
                          <div className="offer-dashboard__panel">
                            <div className="offer-dashboard__panel-col">
                              {renderMeta([
                                { label: 'Customer', value: doc.customerName },
                                { label: 'City', value: doc.customerCity },
                                {
                                  label: 'Contract date',
                                  value: doc.contractDate
                                    ? formatDate(new Date(doc.contractDate).getTime())
                                    : null,
                                },
                                { label: 'Created', value: formatDate(doc.createdAt) },
                              ])}
                              <button
                                type="button"
                                className="offer-dashboard__primary-btn"
                                onClick={() => handleEditContract(doc.id)}
                              >
                                <i className="fas fa-pen-to-square" aria-hidden="true"></i>
                                Edit contract
                              </button>
                            </div>

                            <div className="offer-dashboard__panel-col">
                              {renderShareCard({
                                link: generateContractLink(doc.id),
                                isPublic: doc.isPublic,
                                onToggle: () => handleToggleContractPublic(doc.id, !doc.isPublic),
                                onCopy: () => copyContractLink(doc.id),
                                onOpen: () => openContractLink(doc.id),
                                copied: copiedLinkId === doc.id,
                                hint: doc.isPublic
                                  ? 'Anyone with this link can open the contract without logging in.'
                                  : 'The contract is private. Sharing or opening it publishes it automatically.',
                              })}
                            </div>
                          </div>
                        </div>
                      )}
                    </React.Fragment>
                  );
                } else if (doc.type === 'valueOffer') {
                  // Render Value-Based Offer row
                  const display = valueOfferDisplay(doc);
                  return (
                    <React.Fragment key={doc.id}>
                      <div
                        className={`offer-dashboard__table-row ${
                          expandedOfferId === doc.id ? "offer-dashboard__table-row--expanded" : ""
                        }`}
                        onClick={() => toggleExpandOffer(doc.id)}
                      >
                        <div className="offer-dashboard__cell offer-dashboard__cell--date">
                          {formatDate(doc.createdAt || doc.updatedAt || Date.now())}
                        </div>
                        <div className="offer-dashboard__cell offer-dashboard__cell--title">
                          <div className="offer-dashboard__title-stack">
                            <span className="offer-dashboard__offer-title">
                              {display.title}
                            </span>
                            <span
                              className="offer-dashboard__badge offer-dashboard__badge--value"
                              title="Value-Based Offer (BonusClub Intelligence)"
                            >
                              <i className="fas fa-gem" aria-hidden="true"></i>
                              Value-based
                            </span>
                            {display.optionsCount > 0 && (
                              <span className="offer-dashboard__hint">
                                {display.optionsCount} option{display.optionsCount === 1 ? '' : 's'}
                              </span>
                            )}
                            {docCustomerName(doc) && (
                              <span className="offer-dashboard__offer-customer">
                                <i className="fas fa-user" aria-hidden="true"></i>
                                <span className="offer-dashboard__offer-customer-name">
                                  {docCustomerName(doc)}
                                </span>
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="offer-dashboard__cell offer-dashboard__cell--price">
                          {display.priceLabel}
                        </div>
                        {renderRowMenu(valueOfferRowActions(doc), display.title)}
                      </div>

                      {expandedOfferId === doc.id && (
                        <div className="offer-dashboard__expanded-content">
                          <div className="offer-dashboard__panel">
                            <div className="offer-dashboard__panel-col">
                              {renderMeta([
                                { label: 'Customer', value: doc?.config?.companyName },
                                { label: 'Contact', value: doc?.config?.invoiceRecipient },
                                {
                                  label: 'Deal value / year',
                                  value: doc?.config?.revenuePerClose
                                    ? formatPrice(Number(doc.config.revenuePerClose) || 0)
                                    : null,
                                },
                                {
                                  label: 'Options',
                                  value: display.optionsCount
                                    ? `${display.optionsCount} · ${display.priceLabel}`
                                    : null,
                                },
                                { label: 'AI model', value: doc?.modelInfo?.model },
                              ])}
                              <button
                                type="button"
                                className="offer-dashboard__primary-btn"
                                onClick={() => handleEditValueOffer(doc.id)}
                              >
                                <i className="fas fa-pen-to-square" aria-hidden="true"></i>
                                Open configurator
                              </button>
                            </div>

                            <div className="offer-dashboard__panel-col">
                              {renderShareCard({
                                link: `${window.location.origin}/v/${userId}+${doc.id}`,
                                onCopy: () => handleShareValueOffer(doc),
                                onOpen: () => openValueOfferLink(doc),
                                copied: copiedLinkId === doc.id,
                                hint: 'Copying or opening publishes the current version, so the customer always sees your latest edits.',
                              })}
                            </div>
                          </div>
                        </div>
                      )}
                    </React.Fragment>
                  );
                } else {
                  // Render offer row (existing code)
                  return (
                    <React.Fragment key={doc.id}>
                      <div 
                        className={`offer-dashboard__table-row ${expandedOfferId === doc.id ? "offer-dashboard__table-row--expanded" : ""}`}
                        onClick={() => toggleExpandOffer(doc.id)}
                      >
                        <div className="offer-dashboard__cell offer-dashboard__cell--date">
                          {formatDate(doc.createdAt)}
                        </div>
                        <div className="offer-dashboard__cell offer-dashboard__cell--title">
                          {doc.logo && (
                            <img 
                              src={doc.logo} 
                              alt="Logo" 
                              className="offer-dashboard__offer-logo" 
                            />
                          )}
                          <div className="offer-dashboard__title-stack">
                            <span className="offer-dashboard__offer-title">{doc.title}</span>
                            {doc.documentType === 'invoice' && (
                              <span className="offer-dashboard__badge offer-dashboard__badge--invoice">
                                Invoice
                              </span>
                            )}
                            {doc.documentType === 'invoice' && doc.invoiceNumber && (
                              <span className="offer-dashboard__hint">#{doc.invoiceNumber}</span>
                            )}
                            {doc.documentType !== 'invoice' && doc.showOfferNumber && doc.offerNumber && (
                              <span className="offer-dashboard__hint">#{doc.offerNumber}</span>
                            )}
                            {doc.isPublic && (
                              <span className="offer-dashboard__badge offer-dashboard__badge--public" title="Reachable via public link">
                                <i className="fas fa-globe" aria-hidden="true"></i>
                                Shared
                              </span>
                            )}
                            {docCustomerName(doc) && (
                              <span className="offer-dashboard__offer-customer">
                                <i className="fas fa-user" aria-hidden="true"></i>
                                <span className="offer-dashboard__offer-customer-name">
                                  {docCustomerName(doc)}
                                </span>
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="offer-dashboard__cell offer-dashboard__cell--price">
                          {formatPrice(doc.discountedTotal)}
                        </div>
                        {renderRowMenu(offerRowActions(doc), doc.title)}
                      </div>
                      
                      {expandedOfferId === doc.id && (
                        <div className="offer-dashboard__expanded-content">
                          <div className="offer-dashboard__panel">
                            <div className="offer-dashboard__panel-col">
                              {renderMeta([
                                {
                                  label: 'Type',
                                  value: doc.documentType === 'invoice' ? 'Invoice' : 'Offer',
                                },
                                {
                                  label: 'Number',
                                  value: doc.documentType === 'invoice'
                                    ? doc.invoiceNumber
                                    : doc.offerNumber,
                                },
                                { label: 'Total', value: formatPrice(doc.discountedTotal) },
                                {
                                  label: 'Services',
                                  value: doc.services?.length
                                    ? `${doc.services.length} item${doc.services.length === 1 ? '' : 's'}`
                                    : null,
                                },
                                { label: 'Created', value: formatDate(doc.createdAt) },
                              ])}
                              <div className="offer-dashboard__panel-actions">
                                <button
                                  type="button"
                                  className="offer-dashboard__primary-btn"
                                  onClick={() => handleEditOffer(doc.id)}
                                >
                                  <i className="fas fa-pen-to-square" aria-hidden="true"></i>
                                  Edit
                                </button>
                                <button
                                  type="button"
                                  className="offer-dashboard__ghost-btn"
                                  onClick={() => handleDuplicateOffer(doc)}
                                >
                                  <i className="fas fa-copy" aria-hidden="true"></i>
                                  Duplicate
                                </button>
                                <button
                                  type="button"
                                  className="offer-dashboard__ghost-btn"
                                  onClick={() => setReissueSource(doc)}
                                  title="Bill the same positions again with today's date"
                                >
                                  <i
                                    className="fas fa-file-invoice-dollar"
                                    aria-hidden="true"
                                  ></i>
                                  {doc.documentType === 'invoice'
                                    ? 'Reissue'
                                    : 'Turn into invoice'}
                                </button>
                              </div>
                            </div>

                            <div className="offer-dashboard__panel-col">
                              {renderShareCard({
                                link: generateOfferLink(doc.id),
                                isPublic: doc.isPublic,
                                onToggle: () => handleTogglePublic(doc.id, !doc.isPublic),
                                onCopy: () => copyOfferLink(doc.id),
                                onOpen: () => openOfferLink(doc.id),
                                copied: copiedLinkId === doc.id,
                                hint: doc.isPublic
                                  ? 'Anyone with this link can open the document without logging in.'
                                  : 'The document is private. Sharing or opening it publishes it automatically.',
                              })}
                            </div>
                          </div>
                        </div>
                      )}
                    </React.Fragment>
                  );
                }
              })}
            </div>
          </div>
          )}
        </div>
      )}

      {reissueSource && (
        <ReissueInvoiceModal
          userId={userId}
          sourceOffer={reissueSource}
          onClose={() => setReissueSource(null)}
          onCreated={({ invoice }) => {
            setReissueSource(null);
            showNotification(
              invoice.invoiceNumber
                ? `Invoice ${invoice.invoiceNumber} created`
                : "Invoice created",
              "success"
            );
          }}
        />
      )}
    </div>
  );
};

export default OfferDashboard; 